import React, { useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { CommandPalette, ThemeProvider, useCommandPalette } from '../../sectepe-ui/index';
import { useAuth } from '../../contexts/AuthContext';
import { documentTitleFor, findRoute } from '../../navigation/routes';
import { hatRecht } from '../../berechtigungen';
import KeinZugriffPage from '../../pages/KeinZugriffPage';
import Sidebar from './Sidebar';
import TopBar from './TopBar';
import { useSidebarState } from './useSidebarState';
import { useAppCommands } from './useAppCommands';
import { ConfirmProvider } from '../ui/ConfirmProvider';
import { LinkedInFlowProvider } from '../../linkedin/useLinkedInFlow';
import DryRunBanner from '../DryRunBanner';
import { RouteErrorBoundary } from '../RouteErrorBoundary';

const PALETTE_LABELS = {
  placeholder: 'Seite oder Aktion suchen…',
  empty: 'Keine Treffer.',
  searching: 'Suche läuft…',
};

/**
 * Geschuetzte Layout-Route: Auth-Schranke (frueher ProtectedRoute), dann
 * Seitenleiste, Kopfzeile, Inhalt und Befehlspalette -- im Aufbau der
 * uebrigen SecTepe-Apps (hr-tool, itsm). Die Seiten selbst rendern ueber
 * <Outlet/>. Fehlt der Person das Recht der Route (Paket 2 Schritt 3),
 * steht an dieser Stelle "Kein Zugriff" -- so greifen auch Lesezeichen
 * und Palette, nicht nur die gefilterte Sidebar.
 */
const AppLayout: React.FC = () => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();
  const sidebar = useSidebarState(location.pathname);
  const palette = useCommandPalette(isAuthenticated);
  const commands = useAppCommands();

  useEffect(() => {
    document.title = documentTitleFor(location.pathname);
  }, [location.pathname]);

  if (isLoading) {
    return (
      <div className="app-loading" role="status" aria-live="polite">
        <div className="loading-spinner" />
        <span className="loading-text">Lädt…</span>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Nach der Login-Pruefung: das Recht der Route, falls es der Person fehlt.
  const entry = findRoute(location.pathname);
  const fehlendesRecht = entry?.recht && !hatRecht(user, entry.recht) ? entry.recht : undefined;

  return (
    <ConfirmProvider>
    <LinkedInFlowProvider>
    <div className="app-shell" data-drawer-open={sidebar.mobileOpen ? 'true' : 'false'}>
      <a href="#main-content" className="app-skip-link">Zum Inhalt springen</a>
      <Sidebar state={sidebar} />
      {sidebar.mobileOpen && (
        <div className="app-backdrop" onClick={sidebar.closeMobile} aria-hidden="true" data-testid="app-backdrop" />
      )}
      <div className="app-content">
        <TopBar mobileOpen={sidebar.mobileOpen} onOpenMobile={sidebar.openMobile} onOpenSearch={palette.openPalette} />
        {/* Dry-Run-Hinweis (OUTREACH_DRY_RUN): nur hinter der Auth-Schranke,
            damit der Health-Aufruf erst mit gueltiger Sitzung laeuft. */}
        <DryRunBanner />
        <main id="main-content" className="app-main" tabIndex={-1}>
          {fehlendesRecht ? (
            <KeinZugriffPage recht={fehlendesRecht} />
          ) : (
            // key=pathname: Beim Seitenwechsel wird die Schranke neu gemountet,
            // sodass ein Fehler auf einer Seite nicht an der naechsten klebt.
            <RouteErrorBoundary key={location.pathname}>
              <Outlet />
            </RouteErrorBoundary>
          )}
        </main>
      </div>
      {/* Die Palette braucht den Theme-Kontext des Kits; das Thema bleibt
          hell, weil die App selbst (noch) keinen Dunkelmodus hat. */}
      <ThemeProvider defaultTheme="light">
        <CommandPalette
          open={palette.open}
          onClose={palette.closePalette}
          commands={commands}
          labels={PALETTE_LABELS}
        />
      </ThemeProvider>
    </div>
    </LinkedInFlowProvider>
    </ConfirmProvider>
  );
};

export default AppLayout;
